import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import type {
  SvdNativeCardVariant,
  SvdNativeProduct,
  SvdPromoConfig,
  SvdSocialProofData,
} from '@src/types/svd-product-card.types'

/**
 * Raw SAND `displayConfig.displayType` wire values (README §8). The swatch renders per mode: a hex
 * chip, an image swatch, or a text chip.
 */
export const SVD_SWATCH_DISPLAY_TYPE = {
  HEX_COLOR: 'HEX_COLOR',
  IMAGE: 'IMAGE',
  TEXT: 'TEXT',
} as const

// A sold-out variant suppresses promo pills (parity with the legacy `getVariantTags`).
const SOLD_OUT_TAG_VALUE = 'sold out'

/**
 * Picks the initial selected variant for an SVD card. Variant-id keyed (not color) — falls back to
 * the first variant. Mirrors the legacy `getInitialSelectedVariant` but on the native SVD type.
 */
export function getInitialSvdVariant(
  cardVariants: SvdNativeCardVariant[],
  selectedVariantId?: number,
): SvdNativeCardVariant | undefined {
  if (!selectedVariantId) return cardVariants[0]
  return cardVariants.find(variant => variant.variantId === selectedVariantId) ?? cardVariants[0]
}

/**
 * Whether the card should render a swatch row: only when the product exposes a real anchor
 * dimension (`displayConfig` non-null) on more than one variant. No-VDA products (null config,
 * single variant) render without swatches.
 */
export function shouldRenderSvdSwatches(product: SvdNativeProduct): boolean {
  const swatchable = product.cardVariants.filter(variant => variant.displayConfig !== null)
  return swatchable.length > 1
}

/**
 * Swatchable variants (real `displayConfig`) capped at `limit`, plus the count hidden beyond the cap.
 * Mirrors the legacy card's `optionLimit` + "+N" overflow so a many-variant tile doesn't overflow.
 */
export function getSvdSwatchVariants(
  cardVariants: SvdNativeCardVariant[],
  limit: number,
): { visible: SvdNativeCardVariant[]; overflowCount: number } {
  const swatchable = cardVariants.filter(variant => variant.displayConfig !== null)
  return { visible: swatchable.slice(0, limit), overflowCount: Math.max(0, swatchable.length - limit) }
}

/**
 * The currently-active promo on a variant, or null. Mirrors the legacy `getValidPromo`: keeps only
 * offers whose `[startTime, endTime]` (UTC-naive strings) brackets now, then dedupes their `tags`
 * and concatenates their offer messages. Used to surface promo pills (Y1); pricing fields are left
 * for the deferred pricing work.
 */
export function getSvdActivePromo(promoConfig: SvdPromoConfig[]): { tags: string[]; promoOfferMessage: string } | null {
  if (promoConfig.length === 0) return null

  const now = Date.now()
  const active = promoConfig.filter(promo => {
    const start = Date.parse(promo.startTime + 'Z')
    const end = Date.parse(promo.endTime + 'Z')
    return start <= now && end >= now
  })
  if (active.length === 0) return null

  const seen = new Set<string>()
  const tags: string[] = []
  let promoOfferMessage = ''
  for (const promo of active) {
    promoOfferMessage += promo.promoOfferMessage
    for (const tag of promo.tags) {
      if (!seen.has(tag)) {
        seen.add(tag)
        tags.push(tag)
      }
    }
  }
  return { tags, promoOfferMessage }
}

/**
 * Tags to render over the card image for the selected variant. Backend variant tags by default;
 * an active promo's pills replace them (Y1) unless the variant is sold out — sold-out always wins.
 * Mirrors the legacy `getVariantTags` precedence on the native SVD shape.
 */
export function getSvdApplicableTags(variant: SvdNativeCardVariant | undefined): ProductCardTag[] {
  if (!variant) return []

  const variantTags: ProductCardTag[] = variant.tags.map(tag => ({
    value: tag.value,
    displayName: tag.displayName,
    tagType: tag.tagType as ProductCardTag['tagType'],
  }))

  const isSoldOut = variantTags.some(tag => tag.value.toLowerCase() === SOLD_OUT_TAG_VALUE)
  const activePromo = getSvdActivePromo(variant.promoConfig)
  if (!isSoldOut && activePromo && activePromo.tags.length > 0) {
    return activePromo.tags.map(value => ({ value, tagType: 'PRIMARY' as ProductCardTag['tagType'] }))
  }
  return variantTags
}

/**
 * Human-facing social-proof labels for a variant (Y2): stock urgency + recent-sales, in that order.
 * Uses only the backend `*DisplayName` copy (no synthesized strings, no new TMS keys); empty when
 * the wire carries no social proof — which is every product today.
 */
export function getSvdSocialProofLabels(socialProof: SvdSocialProofData | null | undefined): string[] {
  if (!socialProof) return []
  return [socialProof.stockTagDisplayName, socialProof.soldRecentlyTagDisplayName].filter((label): label is string =>
    Boolean(label),
  )
}
